import { apiFetch } from './api';

export const CASH_GATEWAY_NAME = 'Cash';
export const CIB_GATEWAY_NAME = 'CIB Standard';

const paymentMethodsCache = new Map();

async function parsePaymentError(res, fallback) {
  try {
    const data = await res.json();
    if (data?.detail) return String(data.detail);
    if (data?.message) return String(data.message);
    const parts = Object.entries(data ?? {}).map(
      ([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`,
    );
    if (parts.length) return parts.join(' · ');
  } catch { /* keep fallback */ }
  return fallback;
}

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

/** GET /core/payment-gateway/?slug={slug}&format=json */
export async function fetchPaymentGateways(storeSlug) {
  const params = new URLSearchParams({ format: 'json' });
  if (storeSlug) params.set('slug', storeSlug);
  const res = await apiFetch(`/core/payment-gateway/?${params.toString()}`);
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await parsePaymentError(res, 'Impossible de charger les passerelles de paiement'));
  }
  return normalizeList(await res.json());
}

/** GET /store/{slug}/payment-methods/?format=json */
export async function fetchStorePaymentMethods(slug, { force = false } = {}) {
  if (!force && paymentMethodsCache.has(slug)) return paymentMethodsCache.get(slug);

  const res = await apiFetch(`/store/${slug}/payment-methods/?format=json`);
  if (!res.ok) {
    throw new Error(await parsePaymentError(res, 'Impossible de charger les méthodes de paiement'));
  }
  const methods = normalizeList(await res.json());
  paymentMethodsCache.set(slug, methods);
  return methods;
}

export function clearPaymentMethodsCache(slug) {
  if (slug) paymentMethodsCache.delete(slug);
  else paymentMethodsCache.clear();
}

export function findGateway(gateways, name) {
  return (gateways ?? []).find(
    (g) => String(g?.name ?? '').toLowerCase() === String(name).toLowerCase(),
  ) ?? null;
}

export function findStoreMethod(methods, gatewayName) {
  return (methods ?? []).find(
    (m) => String(m.gateway?.name ?? '').toLowerCase() === String(gatewayName).toLowerCase(),
  ) ?? null;
}

export function findStoreMethodByGatewayId(methods, gatewayId) {
  const id = Number(gatewayId);
  return (methods ?? []).find((m) => Number(m.gateway?.id) === id) ?? null;
}

export function isGatewayActive(methods, gatewayId) {
  return Boolean(findStoreMethodByGatewayId(methods, gatewayId));
}

export function isCibConfigured(method) {
  const creds = method?.credentials;
  if (!creds || typeof creds !== 'object') return false;
  return Object.values(creds).some((v) => String(v ?? '').trim() !== '');
}

/** POST /store/{slug}/payment-methods/?format=json { gateway, details } */
export async function createPaymentMethod(storeSlug, { gateway, details = '' } = {}) {
  const res = await apiFetch(`/store/${storeSlug}/payment-methods/?format=json`, {
    method: 'POST',
    body: JSON.stringify({
      gateway: Number(gateway),
      details: String(details ?? ''),
    }),
  });
  if (!res.ok) {
    throw new Error(await parsePaymentError(res, 'Impossible d\'activer la méthode de paiement'));
  }
  clearPaymentMethodsCache(storeSlug);
  try {
    return await res.json();
  } catch {
    return { gateway, details };
  }
}

/** PUT /store/{slug}/payment-methods/?format=json — id in body (KIO API pattern). */
export async function updatePaymentMethod(storeSlug, methodId, body) {
  const res = await apiFetch(`/store/${storeSlug}/payment-methods/?format=json`, {
    method: 'PUT',
    body: JSON.stringify({ id: methodId, ...body }),
  });
  if (!res.ok) {
    throw new Error(await parsePaymentError(res, 'Impossible de mettre à jour la méthode de paiement'));
  }
  clearPaymentMethodsCache(storeSlug);
  try {
    return await res.json();
  } catch {
    return { id: methodId, ...body };
  }
}

/** DELETE /store/{slug}/payment-methods/?format=json { ids: [...] } */
export async function deletePaymentMethod(storeSlug, methodIds) {
  const ids = (Array.isArray(methodIds) ? methodIds : [methodIds])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) {
    throw new Error('Méthode de paiement introuvable.');
  }

  const res = await apiFetch(`/store/${storeSlug}/payment-methods/?format=json`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
  if (!res.ok && res.status !== 404 && res.status !== 204) {
    throw new Error(await parsePaymentError(res, 'Impossible de désactiver la méthode de paiement'));
  }
  clearPaymentMethodsCache(storeSlug);
  return true;
}

/** Active ou désactive une passerelle pour la boutique. */
export async function setPaymentMethodActive(storeSlug, gateway, methods, active) {
  const existing = findStoreMethodByGatewayId(methods, gateway.id);
  if (active && !existing) {
    await createPaymentMethod(storeSlug, { gateway: gateway.id, details: '' });
  } else if (!active && existing?.id) {
    await deletePaymentMethod(storeSlug, existing.id);
  }
  return fetchStorePaymentMethods(storeSlug, { force: true });
}

/**
 * Applique un mode paiement (cash / cib). Les ajouts sont faits avant les
 * suppressions pour respecter la contrainte « au moins une méthode active ».
 */
export async function applyStorePaymentMode(storeSlug, gateways, methods, { cash, cib }) {
  const targets = [
    { gateway: findGateway(gateways, CASH_GATEWAY_NAME), active: cash },
    { gateway: findGateway(gateways, CIB_GATEWAY_NAME), active: cib },
  ];

  let current = methods;

  for (const { gateway, active } of targets) {
    if (!gateway || !active) continue;
    const isActive = Boolean(findStoreMethodByGatewayId(current, gateway.id));
    if (!isActive) {
      current = await setPaymentMethodActive(storeSlug, gateway, current, true);
    }
  }

  for (const { gateway, active } of targets) {
    if (!gateway || active) continue;
    const isActive = Boolean(findStoreMethodByGatewayId(current, gateway.id));
    if (isActive) {
      current = await setPaymentMethodActive(storeSlug, gateway, current, false);
    }
  }

  return current;
}
